import PageSpinner from "@ui-kit/feedback/PageSpinner/PageSpinner.web";
import EventPasswordModal from "../EventPasswordModal/EventPasswordModal.web";
import { EventAuthorizationProps } from "./EventAuthorization.props";
import { useEventAuthorization } from "./useEventAuthorization";

/**
 * Wraps event content with password authorization.
 *
 * Children are only rendered once the user is authorized.
 * This is intentional -- password-protected events rely on the frontend
 * gate to prevent interaction with ticket elements, avoiding the need
 * for server-side password checks on every add-to-cart call.
 *
 * For branded pages, non-password events resolve synchronously on mount
 * so children render immediately with no flash.
 *
 * For legacy pages, pass `gateChildrenUntilReady` to also wait for
 * ticketsAuthorizedByPassword to resolve before showing children,
 * preventing content flicker during the async auth check.
 */
const EventAuthorization = (props: EventAuthorizationProps) => {
  const {
    eventData,
    children,
    onClose,
    gateChildrenUntilReady,
    ticketsAuthorizedByPassword,
  } = props;
  const {
    eventPasswordAuthorized,
    showPasswordModal,
    showPageSpinner,
    onAuthorize,
  } = useEventAuthorization(props);

  // Legacy pages need children gated until the async auth check resolves
  // (ticketsAuthorizedByPassword becomes non-null) AND the spinner is done.
  // Without this, content flickers as it renders before data is ready.
  const isReady = gateChildrenUntilReady
    ? !showPageSpinner && ticketsAuthorizedByPassword !== null
    : true;

  return (
    <>
      {showPageSpinner && <PageSpinner />}
      {eventData && (
        <EventPasswordModal
          showPasswordAuth={showPasswordModal}
          onAuthorize={onAuthorize}
          eventName={eventData.name ?? ""}
          eventSlug={eventData.slug ?? ""}
          eventId={eventData.id}
          passwordMessage={eventData.password_message ?? undefined}
          onCloseModal={onClose}
        />
      )}
      {eventPasswordAuthorized && isReady && children}
    </>
  );
};

export default EventAuthorization;
